import crypto from 'crypto';

import appConfig from '@nerdwallet/app-config';

export function isProductTypeEnabled(productType) {
  return appConfig.BOT_DETECTION_ENABLED_PRODUCT_TYPES.includes(productType);
}
/**
 * Validates a token generated by Marketplace.
 *
 * A marketplace redirect validation token is constructed the following way;
 *
 * Given the following:
 *   TIME = the current time as a unix epoch 64bit unsigned integer.
 *   USER_ID = an ID used to identify a user (such as cookie_id)
 *   ENTITY_ID = the entity ID of the Offer, Product or Institution being redirected.
 *   SECRET = a shared secret used by interstitial-client and marketplace.
 *   PLAIN = TIME + USER_ID + ENTITY_ID
 *   DIGEST = SHA-256-HMAC(SECRET, PLAIN)
 *   TOKEN = TIME + USER_ID + DIGEST
 *
 * The TOKEN is then base64 encoded and URI encoded for use within the URI parameter list.
 *
 * @param {string} userId - the user identifying token used by Marketplace to generate the token. This is by convention VISITOR_TOKEN.
 * @param {string} entityId - the UUID of the Marketplace entity that the redirect link was generated for.
 * @param {string} tokenToValidate - the URI encoded, base64 encoded, Marketplace token to validate.

 * @return {object} response
 * @return {boolean} response.isValid - Is the token valid?
 * @param {string} response.tokenCreationTimeInSec - Time the token was created (in seconds)
 */
export function validateMarketplaceToken(
  userId,
  entityId,
  tokenToValidate,
  productType = 'null'
) {
  if (
    tokenToValidate === undefined ||
    tokenToValidate === '' ||
    userId === undefined ||
    entityId === undefined ||
    !isProductTypeEnabled(productType)
  ) {
    return { isValid: false, tokenCreationTimeInSec: null };
  }
  try {
    // URI decodes the mvt token from marketplace
    const decodedURI = decodeURIComponent(tokenToValidate);
    // Base64 decodes the token
    const base64Decoded = Buffer.from(decodedURI, 'base64').toString();
    // Splits the token on : so that it is returns an array containing the token time of creation, cookie id, and digest
    const splitToken = base64Decoded.split(':');
    // Assigns each part of array to appropriate variable
    const time = splitToken[0];
    const cookieId = splitToken[1];
    const digest = splitToken[2];
    // Creates a plain byte array to make a new digest to validate token
    const plain = time + cookieId + entityId;
    const digestToValidate = crypto
      .createHmac('sha256', appConfig.APPLY_LINK_VALIDATION_SECRET)
      .update(plain)
      .digest('hex');
    // Current time in seconds
    const currentTimeInSec = Math.floor(Date.now() / 1000);
    // Turns the time from nanoseconds to seconds
    const tokenCreationTimeInSec = Math.floor(parseInt(time, 10) / 1000000000);
    // Current time limit before token expires in seconds. This equates to one hour
    const timeLimit = appConfig.MAX_SECONDS_FOR_VALIDATION;
    // Verifies that the token hasn't expired and the token digest matches the digest we produced
    const isTimeValid = currentTimeInSec < tokenCreationTimeInSec + timeLimit;
    const isDigestValid = digest === digestToValidate;
    const isValid = isDigestValid && isTimeValid;
    return { isValid, tokenCreationTimeInSec };
  } catch (e) {
    return {
      isValid: false,
      tokenCreationTimeInSec: null,
    };
  }
}
